import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, Router } from '@angular/router';
import { filter, firstValueFrom, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { WebStorageService } from './web-storage.service';

/** The drop-off points that ask the exit question. Keep in step with the backend list. */
export type ExitQuestionStep =
    | 'deposit_asset_details'
    | 'deposit_photos'
    | 'deposit_custody_payment'
    | 'kyc_start'
    | 'loan_request'
    | 'loan_refinance'
    | 'loan_counter_offer'
    | 'loan_resale'
    | 'resale_counter_offer'
    | 'sale_listing';

export interface ExitQuestionReason {
    id: string;
    label: string;
}

export interface ExitQuestionStepReasons {
    name: string;
    /** The popup's title, for example "Leaving your deposit?". */
    title?: string;
    /** One line under the title saying what is kept and what we ask. */
    prompt?: string;
    /** The text box's hint for this step. */
    placeholder?: string;
    reasons: ExitQuestionReason[];
}

export interface ExitQuestionAnswer {
    step: ExitQuestionStep;
    reason_id: string | null;
    text: string | null;
}

/** A page the member can leave mid-flow: says which step to ask about, or null for none. */
export interface ExitQuestionPage {
    exitQuestionStep(): ExitQuestionStep | null;
}

/** Opens the popup for a step; resolves once it has closed, whatever the member did. */
type ExitQuestionOpener = (step: ExitQuestionStep) => Promise<void>;

/** The question open in this tab, so a reload shows it again: `{ step, nextUrl }`. */
const PENDING_KEY = 'rw_exit_question_pending';

interface PendingExitQuestion {
    step: ExitQuestionStep;
    /** Where the member was going when the popup opened. */
    nextUrl: string;
}

/**
 * The exit question's calls and its single popup. The reasons list is fetched once per app
 * session and shared by every step; a failed fetch is not cached, so the next open tries again.
 * The member is taken from the session on the server, so no user id is ever sent.
 *
 * The popup lives once in the app shell and registers itself here; the leave guard asks through
 * `ask()`. It asks on every leave, with no cap: every answer is worth keeping.
 *
 * An open question survives a reload: it is kept in sessionStorage (this tab only) until the popup
 * closes, and `restorePending()` shows it again on the next start, then takes the member on to the
 * page they were going to.
 *
 * A sign-out (log out, wallet disconnect, session expiry) is not the member choosing to leave, and
 * the answer could not be saved without a session: `endSession()` closes an open popup, drops the
 * pending one and stops the next leave from asking.
 */
@Injectable({
    providedIn: 'root'
})
export class ExitQuestionService {
    private reasons?: Promise<Record<string, ExitQuestionStepReasons>>;
    private opener?: ExitQuestionOpener;
    private open = false;
    /** The next leave is the restored question's own navigation, which must not ask again. */
    private skipNextLeave = false;
    /** A sign-out has started; cleared once the navigation it causes has finished. */
    private signingOut = false;
    private readonly sessionEndedSubject = new Subject<void>();
    /** Emits when a sign-out starts, so an open popup can close. */
    readonly sessionEnded$ = this.sessionEndedSubject.asObservable();

    private readonly router = inject(Router);
    private readonly storage = inject(WebStorageService);

    constructor(private http: HttpClient) {
        // Optional: test doubles of Router often have no event stream.
        this.router.events
            ?.pipe(filter((event) => event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError))
            .subscribe(() => (this.signingOut = false));
    }

    /** Reasons per step; an empty object when the call fails, so the form still takes text. */
    getReasons(): Promise<Record<string, ExitQuestionStepReasons>> {
        if (!this.reasons) {
            this.reasons = firstValueFrom(this.http.get<any>(`${environment.API_BASE_URL_V3}/exit-question/reasons`))
                .then((response) => response?.data?.steps ?? {})
                .catch(() => {
                    this.reasons = undefined;
                    return {};
                });
        }
        return this.reasons;
    }

    /** Sends one answer; rejects with the HttpErrorResponse on 400 or 429. */
    submit(body: ExitQuestionAnswer): Promise<unknown> {
        return firstValueFrom(this.http.post(`${environment.API_BASE_URL_V3}/exit-question`, body));
    }

    /** Called by the popup component in the app shell. */
    registerOpener(opener: ExitQuestionOpener | undefined): void {
        this.opener = opener;
    }

    /** Whether `ask()` would show the popup right now. */
    canAsk(): boolean {
        return !!this.opener && !this.open;
    }

    /** True once, for the navigation `restorePending()` starts; the leave guard lets it through. */
    consumeSkipNextLeave(): boolean {
        const skip = this.skipNextLeave;
        this.skipNextLeave = false;
        return skip;
    }

    /** Called where a sign-out starts. */
    endSession(): void {
        this.signingOut = true;
        this.clearPending();
        if (this.open) this.sessionEndedSubject.next();
    }

    /** Whether a sign-out is under way, so the leave it causes must not ask. */
    isSigningOut(): boolean {
        return this.signingOut;
    }

    /**
     * Shows the popup for a step and resolves when it closes. Never rejects. Kept as pending
     * while open, so a reload in the meantime brings it back.
     */
    async ask(step: ExitQuestionStep, nextUrl: string): Promise<void> {
        if (!this.opener || !this.canAsk()) return;
        this.open = true;
        this.writePending({ step, nextUrl });
        try {
            await this.opener(step);
        } catch {
            // no-op: a broken popup must never stop the member leaving.
        } finally {
            this.open = false;
            this.clearPending();
        }
    }

    /**
     * After a reload: shows the question that was open before it, then goes on to the page the
     * member was leaving for. Dropped when signed out.
     */
    async restorePending(): Promise<void> {
        const pending = this.readPending();
        if (!pending) return;
        if (!this.storage.getLocalStorage('user') || !this.canAsk()) {
            this.clearPending();
            return;
        }
        await this.ask(pending.step, pending.nextUrl);
        if (pending.nextUrl && this.router.url !== pending.nextUrl) {
            this.skipNextLeave = true;
            await this.router.navigateByUrl(pending.nextUrl).catch(() => false);
            this.skipNextLeave = false;
        }
    }

    private readPending(): PendingExitQuestion | null {
        try {
            const pending = JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null');
            return pending?.step ? pending : null;
        } catch {
            return null;
        }
    }

    private writePending(pending: PendingExitQuestion): void {
        try {
            sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
        } catch {
            // no-op: without storage a reload simply loses the question.
        }
    }

    private clearPending(): void {
        try {
            sessionStorage.removeItem(PENDING_KEY);
        } catch {
            // no-op
        }
    }
}
